/**
 * One supplier's summary card on the RFQ Responses tab.
 *
 * Split out of ResponseComparison to keep that file under the size limit.
 * Shows totals and score, the vendor's uploaded certificates with the
 * customer's verify / reject controls, the negotiation state (request a
 * revised quote, rounds so far, what the previous quote was) and Award.
 */
import { useState } from 'react';
import { rfqAPI } from '../../lib/api';
import type { RFQ, SupplierSummary, SupplierResponseListItem } from '../../lib/api';
import { getApiErrorMessage } from '../../lib/api/types/common';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Award, Trophy, Clock, Loader2, MessageSquareReply, Check, X, History } from 'lucide-react';
import { toast } from 'sonner';

interface ResponseSupplierCardProps {
  rfq: RFQ;
  supplier: SupplierSummary;
  /** The supplier's response row (certificates, revisions). */
  response?: SupplierResponseListItem;
  isLowest: boolean;
  scoreWeights?: { price: number; lead_time: number; performance: number };
  formatCurrency: (value: string | number) => string;
  formatDate: (value: string) => string;
  onAward: (supplier: SupplierSummary) => void;
  onRequestRevision: (supplier: SupplierSummary, responseId: number) => void;
  onDownloadCertificate: (id: number, fileName: string) => void;
  downloadingCertId: number | null;
  /** Anything on the card changed server-side (certificate reviewed) — refetch. */
  onChanged: () => void;
}

export default function ResponseSupplierCard({
  rfq, supplier, response, isLowest, scoreWeights, formatCurrency, formatDate,
  onAward, onRequestRevision, onDownloadCertificate, downloadingCertId, onChanged,
}: ResponseSupplierCardProps) {
  const [reviewingId, setReviewingId] = useState<number | null>(null);
  const [rejectingId, setRejectingId] = useState<number | null>(null);
  const [rejectReason, setRejectReason] = useState('');
  const [showHistory, setShowHistory] = useState(false);

  const certs = response?.certificate_submissions ?? [];
  const revisions = response?.revisions ?? [];
  const rfqOpen = rfq.status === 'sent' || rfq.status === 'submitted';
  const revisionOpen = supplier.revision_requested === true;

  const verify = async (id: number) => {
    try {
      setReviewingId(id);
      await rfqAPI.verifyCertificateSubmission(id);
      toast.success('Certificate verified');
      onChanged();
    } catch (err: unknown) {
      console.error('Error verifying certificate:', err);
      toast.error(getApiErrorMessage(err, 'Could not verify the certificate'));
    } finally {
      setReviewingId(null);
    }
  };

  const reject = async (id: number) => {
    if (!rejectReason.trim()) {
      toast.error('Give the supplier a reason for the rejection.');
      return;
    }
    try {
      setReviewingId(id);
      await rfqAPI.rejectCertificateSubmission(id, { reason: rejectReason.trim() });
      toast.success('Certificate rejected');
      setRejectingId(null);
      setRejectReason('');
      onChanged();
    } catch (err: unknown) {
      console.error('Error rejecting certificate:', err);
      toast.error(getApiErrorMessage(err, 'Could not reject the certificate'));
    } finally {
      setReviewingId(null);
    }
  };

  return (
    <Card className={isLowest ? 'border-green-500 border-2' : ''} data-testid={`response-card-${supplier.id}`}>
      <CardHeader className="pb-2">
        <div className="flex justify-between items-start gap-2">
          <CardTitle className="text-lg">{supplier.name}</CardTitle>
          <div className="flex flex-col items-end gap-1">
            {supplier.recommended && (
              <Badge className="bg-blue-600"><Trophy className="h-3 w-3 mr-1" /> Recommended</Badge>
            )}
            {isLowest && <Badge className="bg-green-500">Lowest price</Badge>}
            {revisionOpen && (
              <Badge className="bg-purple-600" title="Waiting for the supplier's revised quote">
                Revision requested{supplier.revision_round ? ` · round ${supplier.revision_round}` : ''}
              </Badge>
            )}
            {!revisionOpen && (supplier.revision_round ?? 0) > 0 && (
              <Badge variant="outline" className="text-purple-700 border-purple-300">
                Revised {supplier.revision_round} {supplier.revision_round === 1 ? 'time' : 'times'}
              </Badge>
            )}
          </div>
        </div>
        {supplier.score !== null && supplier.score !== undefined && (
          <div
            className="text-sm text-muted-foreground"
            title={supplier.score_breakdown
              ? `Price ${supplier.score_breakdown.price} · Lead time ${supplier.score_breakdown.lead_time} · Supplier OTIF ${supplier.score_breakdown.otif_percent ?? 'n/a'}%`
              : undefined}
          >
            Score <span className="font-semibold text-foreground tabular-nums">{supplier.score}</span> / 100
            {scoreWeights && (
              <span className="ml-1 text-xs">(price {scoreWeights.price}% · lead time {scoreWeights.lead_time}% · OTIF {scoreWeights.performance}%)</span>
            )}
          </div>
        )}
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="space-y-1">
          <div className="flex justify-between text-sm">
            <span className="text-muted-foreground">Line Items:</span>
            <span>{formatCurrency(supplier.total_quoted)}</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-muted-foreground">Additional Costs:</span>
            <span>{formatCurrency(supplier.additional_costs)}</span>
          </div>
          <div className="flex justify-between font-bold pt-1 border-t">
            <span>Total:</span>
            <span className={isLowest ? 'text-green-600' : ''}>{formatCurrency(supplier.grand_total)}</span>
          </div>
        </div>

        <div className="flex items-center gap-4 text-sm text-muted-foreground">
          <div className="flex items-center gap-1"><Clock className="h-3 w-3" /><span>{supplier.lead_time_days} days</span></div>
          <div><span>Valid: {supplier.validity_days} days</span></div>
        </div>

        {/* Negotiation history: what the supplier quoted before each round. */}
        {revisions.length > 0 && (
          <div className="pt-2 border-t" data-testid="revision-history">
            <button
              type="button"
              className="text-xs font-medium text-purple-700 inline-flex items-center gap-1 hover:underline"
              onClick={() => setShowHistory((v) => !v)}
            >
              <History className="h-3 w-3" />
              {showHistory ? 'Hide' : 'Show'} negotiation history ({revisions.length} {revisions.length === 1 ? 'round' : 'rounds'})
            </button>
            {showHistory && (
              <ul className="mt-2 space-y-2 text-xs">
                {revisions.map((rev) => (
                  <li key={rev.id} className="rounded border bg-muted/30 p-2 space-y-0.5">
                    <div className="font-medium">
                      Round {rev.round_number} · requested {formatDate(rev.requested_at)}
                      {rev.requested_by_name ? ` by ${rev.requested_by_name}` : ''}
                    </div>
                    <div className="whitespace-pre-line">{rev.message}</div>
                    {(rev.target_total || rev.target_lead_time_days !== null) && (
                      <div className="text-muted-foreground">
                        {rev.target_total ? `Target total ${formatCurrency(rev.target_total)}` : ''}
                        {rev.target_total && rev.target_lead_time_days !== null ? ' · ' : ''}
                        {rev.target_lead_time_days !== null ? `Target lead time ${rev.target_lead_time_days} days` : ''}
                      </div>
                    )}
                    <div className="text-muted-foreground">
                      Quote before this round: {formatCurrency(rev.previous_quote?.grand_total ?? '0')}
                      {rev.previous_quote?.lead_time_days !== undefined ? ` · ${rev.previous_quote.lead_time_days} days` : ''}
                      {rev.resubmitted_at
                        ? ` · revised ${formatDate(rev.resubmitted_at)}`
                        : (rfq.status === 'awarded' || rfq.status === 'cancelled')
                          ? ' · not revised before the RFQ closed'
                          : ' · awaiting revised quote'}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        {/* The vendor's uploaded certificates — verified here, alongside the quote. */}
        {certs.length > 0 && (
          <div className="pt-2 border-t space-y-1" data-testid="supplier-certificates">
            <p className="text-xs font-medium text-muted-foreground">Certificates</p>
            {certs.map((c) => (
              <div key={c.id} className="space-y-1">
                <div className="flex items-center justify-between gap-2 text-xs">
                  {/* Private media: `/media/rfq/**` is blocked server-side, so the
                      file comes back through the authenticated download endpoint. */}
                  <button
                    type="button"
                    onClick={() => onDownloadCertificate(c.id, c.file_name)}
                    disabled={downloadingCertId === c.id}
                    className="text-primary hover:underline truncate text-left disabled:opacity-60 inline-flex items-center gap-1"
                    title={c.file_name}
                  >
                    {downloadingCertId === c.id && <Loader2 className="h-3 w-3 animate-spin flex-shrink-0" />}
                    {c.required_cert?.certificate_name || c.file_name}
                    {c.certificate_number ? ` · ${c.certificate_number}` : ''}
                  </button>
                  <Badge
                    variant={c.verification_status === 'verified' ? 'default' : c.verification_status === 'rejected' ? 'destructive' : 'outline'}
                    className="text-[10px] flex-shrink-0"
                    title={c.verified_at ? `${c.verification_status_display} ${formatDate(c.verified_at)}${c.verified_by_name ? ` by ${c.verified_by_name}` : ''}` : undefined}
                  >
                    {c.verification_status_display}
                  </Badge>
                </div>
                {c.verification_status === 'rejected' && c.rejection_reason && (
                  <p className="text-[11px] text-destructive">Reason: {c.rejection_reason}</p>
                )}
                {c.verification_status === 'pending' && rejectingId !== c.id && (
                  <div className="flex gap-1">
                    <Button size="sm" variant="outline" className="h-7 text-xs" disabled={reviewingId === c.id} onClick={() => verify(c.id)}>
                      {reviewingId === c.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <Check className="h-3 w-3 mr-1" />}
                      Verify
                    </Button>
                    <Button size="sm" variant="ghost" className="h-7 text-xs text-destructive" disabled={reviewingId === c.id} onClick={() => { setRejectingId(c.id); setRejectReason(''); }}>
                      <X className="h-3 w-3 mr-1" />
                      Reject
                    </Button>
                  </div>
                )}
                {rejectingId === c.id && (
                  <div className="flex gap-1 items-center">
                    <Input
                      className="h-7 text-xs"
                      value={rejectReason}
                      onChange={(e) => setRejectReason(e.target.value)}
                      placeholder="Reason for rejecting (shown to the supplier)"
                      maxLength={500}
                      disabled={reviewingId === c.id}
                    />
                    <Button size="sm" variant="destructive" className="h-7 text-xs" disabled={reviewingId === c.id || !rejectReason.trim()} onClick={() => reject(c.id)}>
                      {reviewingId === c.id ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Reject'}
                    </Button>
                    <Button size="sm" variant="ghost" className="h-7 text-xs" disabled={reviewingId === c.id} onClick={() => setRejectingId(null)}>Cancel</Button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {rfqOpen && (
          <div className="space-y-2">
            {/* Negotiation: reopen the quote for a revised submission. Only a
                submitted quote can be revised; while a round is open the
                supplier owns the next move. */}
            {response && !revisionOpen && response.response_status === 'submitted' && (
              <Button
                variant="outline"
                className="w-full"
                onClick={() => onRequestRevision(supplier, response.id)}
              >
                <MessageSquareReply className="h-4 w-4 mr-2" />
                Request revised quote
              </Button>
            )}
            {revisionOpen ? (
              <p className="text-xs text-center text-muted-foreground">Award is available once the supplier resubmits.</p>
            ) : (
              <Button
                className="w-full"
                variant={supplier.recommended || isLowest ? 'default' : 'outline'}
                onClick={() => onAward(supplier)}
              >
                <Award className="h-4 w-4 mr-2" />
                Award to {supplier.name}
              </Button>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
